import React, { useState } from "react";
import { Collapse } from "antd";
import Icon from "@ant-design/icons";
import { isEmpty } from "lodash";
import * as Icons from "assets/icons";
import { AICC_SERVING_COLOR, AiccLanguageAction } from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import { isServingEndUsers } from "../languageStatus";
import LanguagePanelActions from "./Actions";
import LanguagePanelHeader from "./Header";
import styles from "./LanguagePanel.module.scss";

const { Panel } = Collapse;

interface Props {
  language: AiccManagedLanguage;
  /** Being re-read after one of its own actions; the header says so. */
  isRefreshing?: boolean;
  onAction?: Partial<Record<AiccLanguageAction, (language: AiccManagedLanguage) => void>>;
  /** Opened from the card's menu: the history belongs to the language, not to one version. */
  onViewVersionHistory?: (language: AiccManagedLanguage) => void;
  children: React.ReactNode;
}

/**
 * One language as a card, like EComProduct/PlanList.
 */
function LanguagePanel({ language, isRefreshing, onAction, onViewVersionHistory, children }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const panelKey = String(language.id);

  return (
    <Collapse
      className={styles.languagePanel}
      // Green while any version is live, grey otherwise — scanning a column of languages for
      // "which of these are switched on" is what the stripe is for, and it is the one question a
      // whole language can answer on its own.
      style={{
        borderLeftColor: isServingEndUsers(language) ? AICC_SERVING_COLOR.serving : AICC_SERVING_COLOR.notServing,
      }}
      // Toggles on the header text, and deliberately not on `extra`, so the action
      // buttons need no guard of their own.
      collapsible="header"
      activeKey={isOpen ? [panelKey] : []}
      // One panel per Collapse, so any key at all means this card is the open one.
      onChange={(keys) => setIsOpen(!isEmpty(keys))}
      expandIcon={({ isActive }) => (
        <Icon
          component={Icons.CareRight}
          className={styles.panelArrow}
          rotate={isActive ? 90 : 0}
          // collapsible="header" wires the header text only, leaving the arrow — the one
          // thing that looks like a toggle — inert without this.
          onClick={() => setIsOpen((open) => !open)}
        />
      )}
    >
      <Panel
        key={panelKey}
        header={<LanguagePanelHeader language={language} isRefreshing={isRefreshing} />}
        extra={
          <LanguagePanelActions language={language} onAction={onAction} onViewVersionHistory={onViewVersionHistory} />
        }
      >
        {children}
      </Panel>
    </Collapse>
  );
}

export default LanguagePanel;
